import i18n from "i18next"
import HttpBackend from "i18next-http-backend"
import { initReactI18next } from "react-i18next"

import hashData from "@/locales-hash.json"

import { defaultLanguage } from "./languageConsts"
import type { UiLocaleCode } from "./uiLocales"
import {
  getUiLocale,
  isUiLocaleCode,
  normalizeLocale,
  toUiLocale,
  uiLocaleCodes,
} from "./uiLocales"

function getActiveUiLocale(): UiLocaleCode {
  return toUiLocale(i18n.language ?? i18n.resolvedLanguage) ?? "en"
}

function syncDocument(language = getActiveUiLocale()) {
  const locale = getUiLocale(language)
  document.documentElement.lang = locale.code
  document.documentElement.dir = locale.dir
  document.title = i18n.t("brand.pageTitle")
}

function syncSmartBanner(language = getActiveUiLocale()) {
  const t = i18n.getFixedT(language)

  const setMeta = (name: string, value: string) => {
    document
      .querySelector(`meta[name="smartbanner:${name}"]`)
      ?.setAttribute("content", value)
  }
  setMeta("title", t("smartbanner.title"))
  setMeta("author", t("smartbanner.author"))
  setMeta("button", t("smartbanner.button"))
  setMeta("close-label", t("smartbanner.closeLabel"))

  const titleEl = document.querySelector(".smartbanner__info__title")
  if (titleEl) titleEl.textContent = t("smartbanner.title")

  const authorEl = document.querySelector(".smartbanner__info__author")
  if (authorEl) authorEl.textContent = t("smartbanner.author")

  const buttonLabelEl = document.querySelector(".smartbanner__button__label")
  if (buttonLabelEl) buttonLabelEl.textContent = t("smartbanner.button")

  const button = document.querySelector(".js_smartbanner__button")
  if (button) button.setAttribute("aria-label", t("smartbanner.button"))

  const closeEl = document.querySelector(".js_smartbanner__exit")
  if (closeEl) closeEl.setAttribute("title", t("smartbanner.closeLabel"))
}

const STORAGE_KEY = "dmeetUiLang"
const PENDING_PROFILE_LOCALE_KEY = "dmeetPendingUiLang"

function readStorage(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStorage(code: string) {
  try {
    localStorage.setItem(STORAGE_KEY, code)
  } catch {
    /* unavailable */
  }
}

function clearStorage() {
  try {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(PENDING_PROFILE_LOCALE_KEY)
  } catch {
    /* unavailable */
  }
}

function readPendingProfileLocale(userId: string): UiLocaleCode | null {
  try {
    const raw = localStorage.getItem(PENDING_PROFILE_LOCALE_KEY)
    if (!raw) return null
    const value = JSON.parse(raw)
    return value?.userId === userId && isUiLocaleCode(value?.locale)
      ? value.locale
      : null
  } catch {
    return null
  }
}

function writePendingProfileLocale(userId: string, locale: UiLocaleCode) {
  try {
    localStorage.setItem(
      PENDING_PROFILE_LOCALE_KEY,
      JSON.stringify({ userId, locale }),
    )
  } catch {
    /* unavailable */
  }
}

function clearPendingProfileLocale(userId: string) {
  try {
    const raw = localStorage.getItem(PENDING_PROFILE_LOCALE_KEY)
    if (!raw || JSON.parse(raw)?.userId === userId) {
      localStorage.removeItem(PENDING_PROFILE_LOCALE_KEY)
    }
  } catch {
    /* unavailable */
  }
}

function getExplicitUrlLocale(): UiLocaleCode | null {
  try {
    const value = new URLSearchParams(window.location.search).get("lang")
    return toUiLocale(value) ?? null
  } catch {
    return null
  }
}

function getStoredLocale(): UiLocaleCode | null {
  const stored = readStorage()
  if (!stored) return null

  const locale = toUiLocale(stored)
  if (!locale) clearStorage()
  return locale ?? null
}

function getBrowserLocale(): UiLocaleCode {
  try {
    const candidates = [navigator.language, ...(navigator.languages ?? [])]
    for (const candidate of candidates) {
      const locale = toUiLocale(candidate)
      if (locale) return locale
    }
  } catch {
    /* unavailable */
  }
  return defaultLanguage
}

function getLanguageFromDevice(): UiLocaleCode {
  return getStoredLocale() ?? getBrowserLocale()
}
async function changeUiLanguage(code: UiLocaleCode): Promise<UiLocaleCode> {
  await i18n.changeLanguage(code)
  syncDocument(code)
  writeStorage(code)
  return code
}

const initialLocale = getExplicitUrlLocale() ?? getLanguageFromDevice()

const initPromise = i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    lng: initialLocale,
    supportedLngs: [...uiLocaleCodes, "cimode"],
    fallbackLng: defaultLanguage,
    debug: false,
    interpolation: {
      escapeValue: false,
    },
    keySeparator: false,
    react: {
      useSuspense: false,
    },
    backend: {
      loadPath: (lng: string) => `/locales/${lng}.json?v=${hashData.hash}`,
    },
  })
  .then(() => {
    const activeLocale = getActiveUiLocale()
    syncDocument(activeLocale)
    syncSmartBanner(activeLocale)
    writeStorage(activeLocale)
  })

i18n.on("languageChanged", () => {
  const activeLocale = getActiveUiLocale()
  syncDocument(activeLocale)
  syncSmartBanner(activeLocale)
  writeStorage(activeLocale)
})

export {
  changeUiLanguage,
  clearPendingProfileLocale,
  clearStorage,
  getExplicitUrlLocale,
  getLanguageFromDevice,
  initPromise,
  isUiLocaleCode,
  normalizeLocale,
  readPendingProfileLocale,
  readStorage,
  STORAGE_KEY,
  syncDocument,
  syncSmartBanner,
  toUiLocale,
  writePendingProfileLocale,
  writeStorage,
}

export default i18n
